import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Collapse, IconButton, Stack, Typography } from '@mui/material';
import { GridApi } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { useCallback, useState } from 'react';
import { ConfirmDeletionDialog } from '../../../Dialog/ConfirmDialog/ConfirmDeletionDialog';
import { FormLoadingButton } from '../../FormComponents';
import { CustomFieldDataGridData, getCustomFieldDataGridData } from './TimeSeriesDataTable';
import { useDeleteCustomFieldDataItem, useUpdateTimeSeriesData } from './useUpdateTimeSeriesData';
import { timeSeriesRowDataToCustomFieldData, validateTimeSeriesData } from './fieldsWithTimeSeriesUtils';

export function DeleteCustomFieldDataRenderer(props: CustomCellRendererProps<CustomFieldDataGridData>) {
  const deleteData = useDeleteCustomFieldDataItem();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);

  const { data, api } = props;

  const onDelete = useCallback(async () => {
    if (!data) {
      return;
    }
    setLoading(true);
    const updatedHistory = await deleteData(data);
    if (updatedHistory) {
      api.setGridOption('rowData', getCustomFieldDataGridData(updatedHistory));
    }
    setLoading(false);
    setShowConfirmation(false);
  }, [deleteData, data, api]);

  return (
    <>
      {showConfirmation && (
        <ConfirmDeletionDialog
          onConfirm={onDelete}
          onClose={() => setShowConfirmation(false)}
          open
          title='Delete Data'
          loading={loading}
        >
          <Typography variant='body1' color='text.secondary'>
            Are you sure you want to delete this item?
          </Typography>
        </ConfirmDeletionDialog>
      )}
      <IconButton onClick={() => setShowConfirmation(true)} title='delete item'>
        <DeleteOutlineIcon />
      </IconButton>
    </>
  );
}

export function SaveCustomFieldData({ api }: { api: GridApi<CustomFieldDataGridData> }) {
  const saveChanges = useUpdateTimeSeriesData();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const onSave = useCallback(async () => {
    const rows = api.getGridOption('rowData');
    if (!rows) {
      return;
    }
    const validationResult = validateTimeSeriesData(rows);
    if (!validationResult.isValid) {
      api.setGridOption('rowData', validationResult.updatedRowData);
      api.redrawRows(); // force redraw to clear error classes from previous validation
      setErrorMessage(validationResult.errorMessage);
      return;
    }
    setErrorMessage('');
    setLoading(true);
    const updatedData = await saveChanges(timeSeriesRowDataToCustomFieldData(rows));
    if (updatedData) {
      api.setGridOption('rowData', getCustomFieldDataGridData(updatedData));
    }
    setLoading(false);
  }, [api, saveChanges]);

  return (
    <Stack width='100%'>
      <Collapse in={!!errorMessage}>
        <Typography color='error' variant='body2' mb={'0.5rem'} mt={'-1.5rem'}>
          {errorMessage}
        </Typography>
      </Collapse>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <FormLoadingButton onClick={onSave} loading={loading} size='medium'>
          Save Changes
        </FormLoadingButton>
      </div>
    </Stack>
  );
}
